import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { ScrollView } from "@shared/client/components/scroll-view/scroll-view";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, pointerWithin, rectIntersection, TouchSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { WebHaptics } from "web-haptics";
import { showToast } from "@shared/client/components/toast/show-toast";
import { useApp } from "@/state/app-context";
import { collectionStore } from "@/data/collection-store";
import { COLLECTION_CARD_PREVIEW_LIMIT } from "@/components/collection-block-view/use-collection-card-previews";
import { createStackFromBlocks } from "@/data/create-stack-from-blocks";
import { SortableContext, sortableKeyboardCoordinates, } from "@dnd-kit/sortable";
import { buildManualOrderScopeKey, manualOrderStore } from "@/state/manual-order-store";
import { SortableBlockViewHost } from "@/components/sortable-block-view-host/sortable-block-view-host";
import { UnfurlBlockHost } from "@/components/unfurl-block-host/unfurl-block-host";
import { PinSlotContext } from "./pin-slot-context";
import { buildFeedItems, mergeSameCollectionBlocks, resolveGroupStrategy, toLocalDateKey, formatDateHeader, } from "./group-helpers";
import { pinItemKey } from "@shared/types/pin";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { mainStreamStore } from "@/data/main-stream-store";
import { pinnedStreamStore } from "@/data/pinned-stream-store";
import { usePinList, bootstrapPinsFromApi } from "@/state/pin-store";
import { BlockViewContainer } from "@/components/block/block-view-container";
import { CollectionBlockView } from "@/components/collection-block-view/collection-block-view";
import { StackBlockView } from "@/components/stack-block-view/stack-block-view";
import { TimelineGap } from "@/components/timeline-view/timeline-gap";
import { useStackUnfurl } from "@/components/stack-unfurl/stack-unfurl";
import { StackFrame } from "@/components/stack-unfurl/stack-frame";
import { useStackNewestMemberAt } from "./use-stack-newest-member-at";
import { track } from "@/lib/analytics";
import "./stream.scss";
// ---------------------------------------------------------------------------
// Pinned helpers (carried over from helpers.ts)
// ---------------------------------------------------------------------------
function orderedPinnedFeedItems(pinOrder, envelopes, collections) {
    if (pinOrder.length === 0)
        return [];
    const blockById = new Map();
    for (const env of envelopes) {
        blockById.set(env.block.id, env);
    }
    const collectionById = new Map();
    if (collections) {
        for (const c of collections) {
            collectionById.set(c.id, c);
        }
    }
    const out = [];
    for (const p of pinOrder) {
        if (p.type === "block") {
            const env = blockById.get(p.id);
            if (env) {
                out.push({ kind: "block", envelope: env, sortAt: env.block.capturedAt });
            }
            continue;
        }
        if (p.type === "collection") {
            const collection = collectionById.get(p.id);
            if (collection) {
                out.push({
                    kind: "collection",
                    collection,
                    sortAt: collection.lastAddedAt ?? collection.createdAt,
                });
            }
        }
    }
    return out;
}
function excludePinnedFromItems(envelopes, collections, pinOrder) {
    if (pinOrder.length === 0)
        return { blocks: envelopes, collections };
    const pinnedKeys = new Set(pinOrder.map(pinItemKey));
    const blocks = envelopes.filter((e) => pinnedKeys.has(pinItemKey({ type: "block", id: e.block.id })) === false);
    const filteredCollections = collections?.filter((c) => pinnedKeys.has(pinItemKey({ type: "collection", id: c.id })) === false);
    return { blocks, collections: filteredCollections };
}
// ---------------------------------------------------------------------------
// Gap helpers (carried over from timeline-view.tsx)
// ---------------------------------------------------------------------------
const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
function sortSegmentsNewestFirst(segs) {
    return [...segs].sort((a, b) => b.coveredTo.localeCompare(a.coveredTo));
}
function dayBelongsToSegment(dateKey, seg, blocks) {
    // Segment ranges track `surfaceAt` (the homepage's sort key); the
    // visible group keys also bucket by `surfaceAt`. Both checks here
    // therefore have to read the same column, otherwise a block whose
    // capturedAt and surfaceAt diverge (post-reprocess) would be
    // bucketed to one date but counted against another segment.
    for (const e of blocks) {
        if (toLocalDateKey(e.block.surfaceAt) !== dateKey)
            continue;
        const t = e.block.surfaceAt;
        if (t >= seg.coveredFrom && t <= seg.coveredTo)
            return true;
    }
    return false;
}
function buildGapPairs(segments) {
    const sorted = sortSegmentsNewestFirst(segments);
    const out = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        const newer = sorted[i];
        const older = sorted[i + 1];
        if (older.coveredTo < newer.coveredFrom) {
            out.push({ newer, older });
        }
    }
    return out;
}
function formatShortDateFromIso(iso) {
    const d = new Date(iso);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}
function gapLabel(newer, older) {
    return `${formatShortDateFromIso(older.coveredTo)} – ${formatShortDateFromIso(newer.coveredFrom)}`;
}
// ---------------------------------------------------------------------------
// Jump-to-date helpers
// ---------------------------------------------------------------------------
function getScrollRangeForTarget(target) {
    if (!target || target === "gap")
        return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(target)) {
        const start = new Date(`${target}T00:00:00.000Z`);
        const end = new Date(start);
        end.setUTCDate(end.getUTCDate() + 7);
        return { after: start.toISOString(), before: end.toISOString() };
    }
    if (/^\d{4}-\d{2}$/.test(target)) {
        const [yStr, mStr] = target.split("-");
        const y = Number(yStr);
        const m = Number(mStr);
        const start = new Date(Date.UTC(y, m - 1, 1));
        const end = m === 12 ? new Date(Date.UTC(y + 1, 0, 1)) : new Date(Date.UTC(y, m, 1));
        return { after: start.toISOString(), before: end.toISOString() };
    }
    return undefined;
}
function jumpTargetDomId(target) {
    return `timeline-jump-${target}`;
}
function firstDateKeyOverlappingRange(dateKeys, blocks, range) {
    // Same `surfaceAt` rule as `dayBelongsToSegment` — the jump-to
    // range slices the timeline on the same column it's sorted on.
    return dateKeys.find((k) => {
        const dayBlocks = blocks.filter((e) => toLocalDateKey(e.block.surfaceAt) === k);
        return dayBlocks.some((e) => e.block.surfaceAt >= range.after && e.block.surfaceAt < range.before);
    });
}
const FLAT_GROUP_KEY = "__all__";
// Stable noop for variants that don't have a collapse affordance (pinned).
const NOOP = () => { };
const StackUnfurlCoordinator = memo(function StackUnfurlCoordinator({ envelopeOverrides, }) {
    useStackUnfurl({ envelopeOverrides });
    return _jsx(_Fragment, {});
});
function feedItemMatchesStackId(item, stackId) {
    if (item.kind === "collection")
        return item.collection.id === stackId;
    if (item.kind === "synthetic-stack")
        return item.id === stackId;
    return false;
}
function findStackItemIndex(items, stackId) {
    if (stackId === undefined)
        return -1;
    return items.findIndex((item) => feedItemMatchesStackId(item, stackId));
}
function paletteIdForStackItem(item, fallbackId) {
    if (item === undefined)
        return fallbackId;
    if (item.kind === "synthetic-stack")
        return item.parent.id;
    if (item.kind === "collection")
        return item.collection.id;
    return fallbackId;
}
const BlockTileHost = memo(function BlockTileHost({ blockId, sortableEnabled, dateChipMode, }) {
    if (sortableEnabled) {
        return _jsx(SortableBlockViewHost, { blockId: blockId, dateChipMode: dateChipMode });
    }
    return _jsx(BlockViewContainer, { blockId: blockId, dateChipMode: dateChipMode });
});
const SyntheticStackTileHost = memo(function SyntheticStackTileHost({ item, isOpen, darkStackTiles, }) {
    const app = useApp();
    const parentIsStack = item.parent.layerIndex === COLLECTION_LAYER.stack;
    const stack = useMemo(() => ({
        id: item.id,
        ownerId: item.parent.ownerId,
        type: item.parent.type,
        title: item.parent.title,
        metadata: item.parent.metadata,
        createdAt: item.sortAt,
        lastAddedAt: item.sortAt,
        layerIndex: COLLECTION_LAYER.stack,
        blockCount: item.blocks.length,
    }), [item]);
    const addedByAccountIds = useMemo(() => {
        const accountIds = [];
        const seenAuthors = new Set();
        for (const env of item.blocks) {
            const accountId = env.block.createdBy;
            if (typeof accountId !== "string" || accountId.length === 0)
                continue;
            if (seenAuthors.has(accountId))
                continue;
            seenAuthors.add(accountId);
            accountIds.push(accountId);
        }
        return accountIds;
    }, [item.blocks]);
    const synthetic = useMemo(() => ({
        previewEnvelopes: item.blocks.slice(0, COLLECTION_CARD_PREVIEW_LIMIT),
        count: item.blocks.length,
        paletteId: item.parent.id,
        addedByAccountIds,
        useRealStackPill: parentIsStack,
        realStackId: parentIsStack ? item.parent.id : undefined,
        realStackParentId: parentIsStack ? item.parent.parentCollectionId : undefined,
    }), [item.blocks, item.parent.id, item.parent.parentCollectionId, parentIsStack, addedByAccountIds]);
    const handleOpen = useCallback((nextStack) => {
        app.toggleStackUnfurl(nextStack);
    }, [app]);
    return (_jsx(StackBlockView, { stack: stack, isOpen: isOpen, onOpen: handleOpen, synthetic: synthetic, darkBg: darkStackTiles === true && parentIsStack === false }));
});
const RealStackTileHost = memo(function RealStackTileHost({ collection, isOpen, darkStackTiles, }) {
    const app = useApp();
    const handleOpen = useCallback((stack) => {
        app.toggleStackUnfurl(stack);
    }, [app]);
    return (_jsx(StackBlockView, { stack: collection, isOpen: isOpen, onOpen: handleOpen, darkBg: darkStackTiles }));
});
const CollectionTileHost = memo(function CollectionTileHost({ collection, onOpenCollection, }) {
    return _jsx(CollectionBlockView, { collection: collection, onOpen: onOpenCollection });
});
const UnfurlItemListHost = memo(function UnfurlItemListHost({ openStackId, openStackItem, envelopes, phase, dateChipMode, }) {
    const app = useApp();
    const registerUnfurlItemRef = useCallback((blockId, el) => {
        app.registerStackUnfurlItemRef(blockId, el);
    }, [app]);
    const isSyntheticStack = openStackItem?.kind === "synthetic-stack";
    const isSyntheticAsRealStack = isSyntheticStack &&
        openStackItem !== undefined &&
        openStackItem.kind === "synthetic-stack" &&
        openStackItem.parent.layerIndex === COLLECTION_LAYER.stack;
    let dragStackId;
    let dragParentId;
    if (openStackItem !== undefined) {
        if (openStackItem.kind === "collection") {
            dragStackId = openStackItem.collection.id;
            dragParentId = openStackItem.collection.parentCollectionId;
        }
        else if (openStackItem.kind === "synthetic-stack") {
            if (openStackItem.parent.layerIndex === COLLECTION_LAYER.stack) {
                dragStackId = openStackItem.parent.id;
                dragParentId = openStackItem.parent.parentCollectionId;
            }
            else {
                dragStackId = openStackId;
                dragParentId = openStackItem.parent.id;
            }
        }
    }
    if (dragStackId === undefined || dragParentId === undefined)
        return _jsx(_Fragment, {});
    const dndDisabled = isSyntheticStack && isSyntheticAsRealStack === false;
    return (_jsx(_Fragment, { children: envelopes.map((env) => {
            const blockId = env.block.id;
            return (_jsx(UnfurlBlockHost, { blockId: blockId, stackId: dragStackId, parentCollectionId: dragParentId, dateChipMode: dateChipMode, isDragEnabled: phase === "open", disableDnd: dndDisabled, registerUnfurlItemRef: registerUnfurlItemRef }, `unfurl-${blockId}`));
        }) }));
});
const StreamGroup = React.memo(({ group, isCollapsed, onToggleCollapse, onOpenCollection, domId, variant = "day", cardDateChipMode = "time", scopeKey, registerScopeElement, darkStackTiles, }) => {
    const app = useApp();
    const sortableEnabled = scopeKey !== undefined;
    const isPinnedSection = variant === "pinned";
    // The `groupByNone` strategy emits a single synthetic group keyed
    // `__all__` whose key isn't a date — `formatDateHeader` would still
    // produce a string, but it would be garbage. Skip the date chip
    // entirely for flat layouts so callers can render an unstructured
    // grid without the sticky sidebar slot.
    const isFlatSection = !isPinnedSection && group.key === FLAT_GROUP_KEY;
    const dayHeader = !isPinnedSection && !isFlatSection ? formatDateHeader(group.key) : undefined;
    // Drag-lifecycle context — drives two data-attributes on the
    // section element:
    //   - `data-active-drag-source`: this section's scopeKey matches
    //     the in-flight drag's source. CSS in `stream.scss` lifts the
    //     whole section above sibling sections so the dragged card
    //     paints above foreign-day cards across the
    //     `.stream-group__cards` isolation boundary.
    //   - `data-drag-disabled`: this section's scopeKey matches the
    //     scope the pointer is currently hovering AND that scope is
    //     foreign to the source. CSS dims this section's cards.
    // Outside of an active drag (or when this section has no
    // `scopeKey`), both attributes resolve to `undefined` and the
    // section reads normally.
    //
    // Two narrow `useStore` selectors instead of a single object read
    // so this StreamGroup only re-renders when ITS scope's
    // active/disabled status flips — non-source / non-foreign groups
    // see boolean values that stay stable across drag-move pointer
    // updates and Zustand's Object.is bails out their re-render.
    const isActiveDragSource = app.useStore((s) => scopeKey !== undefined && scopeKey === s.drag.activeSourceScopeKey);
    const isDragDisabledTarget = app.useStore((s) => scopeKey !== undefined && scopeKey === s.drag.foreignPointerScopeKey);
    // The cards container's DOM element. Stored as state (via a
    // callback ref) rather than `useRef` so child components like
    // `<StackFrame>` re-render once the element is available — a plain
    // ref's `.current` would still be null on first render and the
    // SVG mount would be skipped permanently.
    const [cardsEl, setCardsEl] = useState(null);
    const [isWithinSortableViewport, setIsWithinSortableViewport] = useState(true);
    // Combined ref: drive both the local `cardsEl` state (existing
    // consumers: stack-unfurl row index, StackFrame mount) AND the
    // Stream's per-scope element map (new consumer: foreign-scope
    // probe in `onDragMove`). Wrapping in a callback keeps both
    // callbacks firing on every mount / unmount cycle.
    const setCardsElCombined = useCallback((el) => {
        setCardsEl(el);
        if (scopeKey !== undefined && registerScopeElement !== undefined) {
            registerScopeElement(scopeKey, el);
        }
    }, [scopeKey, registerScopeElement]);
    useEffect(() => {
        if (sortableEnabled === false) {
            setIsWithinSortableViewport(true);
            return;
        }
        if (cardsEl === null || typeof IntersectionObserver === "undefined") {
            setIsWithinSortableViewport(true);
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            setIsWithinSortableViewport(entry?.isIntersecting === true);
        }, { root: null, rootMargin: "100% 0px" });
        observer.observe(cardsEl);
        return () => observer.disconnect();
    }, [cardsEl, sortableEnabled]);
    const sortableLayerEnabled = sortableEnabled && isWithinSortableViewport;
    // Subscribe to a hosting-scoped view of the stack-unfurl slice.
    // Non-hosting groups keep seeing `undefined` / "closed" across
    // every phase transition, so Zustand's Object.is check skips their
    // re-render while the hosting group animates.
    const openStackId = app.useStore((s) => {
        const id = s.stackUnfurl.openStackId;
        return findStackItemIndex(group.items, id) === -1 ? undefined : id;
    });
    const unfurlPhase = app.useStore((s) => findStackItemIndex(group.items, s.stackUnfurl.openStackId) === -1
        ? "closed"
        : s.stackUnfurl.phase);
    const unfurlEnvelopes = app.useStore((s) => findStackItemIndex(group.items, s.stackUnfurl.openStackId) === -1
        ? undefined
        : s.stackUnfurl.envelopes);
    const showUnfurledItems = app.useStore((s) => findStackItemIndex(group.items, s.stackUnfurl.openStackId) === -1
        ? false
        : s.stackUnfurl.showUnfurledItems);
    // Index of the stack item within `group.items` (-1 if the open stack
    // isn't in this group). Cheap derived value — no DOM reads. Matches
    // both real Stack tiles (`kind: "collection"` with a stack
    // layerIndex) and synthetic same-collection groupings
    // (`kind: "synthetic-stack"`) — both render through
    // `<StackBlockView>` and both can be the active unfurl target.
    const stackIdxInGroup = useMemo(() => findStackItemIndex(group.items, openStackId), [group.items, openStackId]);
    const hasOpenStackHere = stackIdxInGroup !== -1;
    // Palette key for the currently-open (or lingering, post-close)
    // stack tile. Mirrors `<StackBlockView>`'s `paletteId` resolution:
    //   - synthetic stack → parent Collection id
    //   - real Stack      → stack id
    // Routing the parent's id keeps the StackFrame's hue locked to the
    // SAME palette entry the closed-state banner uses (which is
    // hashed off `synthetic.paletteId === parent.id` in
    // `<StackBlockView>`). Without this, a synthetic stack's
    // `synthetic-stack:<id>:<date>` id would hash to a random palette
    // entry and the frame + X button would read in different hues
    // from the banner. Falls back to the linger id itself when the
    // open item has already been removed from `group.items` (defensive
    // — the X button + frame both use `getCollectionPaletteColor` so
    // an unrecognized id still resolves to *a* palette entry, just
    // not necessarily the right one).
    const stackPaletteIdForLinger = useMemo(() => {
        if (openStackId === undefined)
            return undefined;
        return paletteIdForStackItem(group.items[stackIdxInGroup], openStackId);
    }, [group.items, stackIdxInGroup, openStackId]);
    // Sticky companion to `lingerStackId` — preserves the palette
    // key across the close FLIP so the fading frame keeps its hue
    // even after `openStackId` clears and the live
    // `stackPaletteIdForLinger` returns to `undefined`. Captures the
    // latest active palette while a stack is open; the cleanup path
    // below (tied to `lingerStackId === undefined` after the linger
    // window) clears it back to `undefined`.
    const [lingerPaletteId, setLingerPaletteId] = useState(undefined);
    useEffect(() => {
        if (stackPaletteIdForLinger !== undefined) {
            setLingerPaletteId(stackPaletteIdForLinger);
        }
    }, [stackPaletteIdForLinger]);
    // Keep the StackFrame mounted briefly after the stack closes so its
    // CSS exit animation can run to completion before the SVG unmounts.
    // Without this, unmounting at `phase=closed` (when `openStackId`
    // becomes undefined) cuts longer exit animations short — the FLIP
    // is done but e.g. the draw-on retraction is still in progress.
    //
    // Strategy: remember the last stack that was open in this group as
    // `lingerStackId`, then clear it after `STACK_FRAME_EXIT_LINGER_MS`.
    // The StackFrame renders with `isVisible=false` during the linger
    // (CSS exit transition plays); after the linger the SVG unmounts.
    const STACK_FRAME_EXIT_LINGER_MS = 600;
    const [lingerStackId, setLingerStackId] = useState(undefined);
    const lingerTimerRef = useRef(undefined);
    useEffect(() => {
        if (hasOpenStackHere && openStackId !== undefined) {
            // New open — cancel any pending linger and track the current id.
            if (lingerTimerRef.current !== undefined) {
                clearTimeout(lingerTimerRef.current);
                lingerTimerRef.current = undefined;
            }
            setLingerStackId(openStackId);
        }
        else if (lingerStackId !== undefined && lingerTimerRef.current === undefined) {
            // Stack just closed in this group — start the exit linger.
            lingerTimerRef.current = window.setTimeout(() => {
                lingerTimerRef.current = undefined;
                setLingerStackId(undefined);
                // Clear the palette companion together with the linger id
                // so a future open doesn't briefly paint with the OLD
                // stack's hue before the active-palette effect runs.
                setLingerPaletteId(undefined);
            }, STACK_FRAME_EXIT_LINGER_MS);
        }
        return () => {
            if (lingerTimerRef.current !== undefined) {
                clearTimeout(lingerTimerRef.current);
            }
        };
    }, [hasOpenStackHere, lingerStackId, openStackId]);
    // Subscribe to the manual-order store for this group's scope so
    // the user's saved arrangement always applies. With drag-to-
    // reorder available on every block (no toggle, gesture-driven),
    // any captured ordering should persist across navigation,
    // reload, and live updates. The reconciled order:
    //   - keeps stored ids that are still in the live block set
    //   - appends new live ids at the end (preserving the user's
    //     existing arrangement when fresh captures land in a
    //     manually-ordered day)
    //   - drops stored ids that are no longer present
    //
    // `scopeKey ?? ""` is a safe lookup key — the store treats empty
    // strings as a no-op read, so non-reorderable surfaces (e.g. the
    // layer-1 Collections grid) read `undefined` and fall through to
    // the live order.
    const storedOrder = manualOrderStore.useOrder(scopeKey ?? "");
    const reconciledBlockIds = useMemo(() => {
        if (!scopeKey)
            return undefined;
        const liveBlockIds = [];
        for (const it of group.items) {
            if (it.kind === "block")
                liveBlockIds.push(it.envelope.block.id);
        }
        return manualOrderStore.reconcile(storedOrder, liveBlockIds);
    }, [scopeKey, storedOrder, group.items]);
    // Walk the reconciled block ids alongside the original item list:
    // each time we'd render a block, dequeue the next reconciled id and
    // use THAT envelope from a lookup map. Non-block items keep their
    // original sortAt-position. Effect: blocks shuffle among themselves
    // while collections / stacks stay where the server placed them.
    const envelopeByBlockId = useMemo(() => {
        const map = new Map();
        for (const it of group.items) {
            if (it.kind === "block") {
                map.set(it.envelope.block.id, it.envelope);
            }
        }
        return map;
    }, [group.items]);
    // The unfurl items splice in *immediately after the stack tile*
    // (at `stackIdxInGroup`) rather than at the end of the stack tile's
    // row. The individual tile hosts memoize their derived objects, so
    // a hosting-group phase change only reaches the tile whose primitive
    // open/closed props actually changed.
    const renderedChildren = useMemo(() => {
        const nodes = [];
        let nextReconciledBlockIdx = 0;
        group.items.forEach((item, idx) => {
            if (item.kind === "block") {
                let blockId;
                if (reconciledBlockIds && nextReconciledBlockIdx < reconciledBlockIds.length) {
                    blockId = reconciledBlockIds[nextReconciledBlockIdx];
                    nextReconciledBlockIdx += 1;
                }
                else {
                    blockId = item.envelope.block.id;
                }
                if (reconciledBlockIds && !envelopeByBlockId.has(blockId)) {
                    blockId = item.envelope.block.id;
                }
                nodes.push(_jsx(BlockTileHost, { blockId: blockId, sortableEnabled: sortableLayerEnabled, dateChipMode: cardDateChipMode }, blockId));
            }
            else if (item.kind === "synthetic-stack") {
                nodes.push(_jsx(SyntheticStackTileHost, { item: item, isOpen: openStackId === item.id && unfurlPhase !== "closing", darkStackTiles: darkStackTiles }, item.id));
            }
            else if (item.collection.layerIndex === COLLECTION_LAYER.stack) {
                nodes.push(_jsx(RealStackTileHost, { collection: item.collection, isOpen: openStackId === item.collection.id && unfurlPhase !== "closing", darkStackTiles: darkStackTiles }, item.collection.id));
            }
            else {
                nodes.push(_jsx(CollectionTileHost, { collection: item.collection, onOpenCollection: onOpenCollection }, item.collection.id));
            }
            if (openStackId !== undefined &&
                hasOpenStackHere &&
                showUnfurledItems &&
                unfurlEnvelopes !== undefined &&
                idx === stackIdxInGroup) {
                nodes.push(_jsx(UnfurlItemListHost, { openStackId: openStackId, openStackItem: group.items[stackIdxInGroup], envelopes: unfurlEnvelopes, phase: unfurlPhase, dateChipMode: cardDateChipMode }, `unfurl-items-${openStackId}`));
            }
        });
        return nodes;
    }, [
        group.items,
        reconciledBlockIds,
        envelopeByBlockId,
        sortableLayerEnabled,
        cardDateChipMode,
        openStackId,
        unfurlPhase,
        darkStackTiles,
        onOpenCollection,
        hasOpenStackHere,
        showUnfurledItems,
        unfurlEnvelopes,
        stackIdxInGroup,
    ]);
    return (_jsxs("section", { className: `stream-group${isPinnedSection ? " stream-group--pinned" : ""}${isFlatSection ? " stream-group--flat" : ""}`, id: domId, "aria-label": isPinnedSection ? "Pinned blocks" : undefined, "data-active-drag-source": isActiveDragSource ? "" : undefined, "data-drag-disabled": isDragDisabledTarget ? "" : undefined, children: [_jsxs("div", { className: "stream-group__header", children: [isPinnedSection ? (_jsx("div", { className: "stream-group__chip stream-group__chip--pinned", children: _jsx("span", { className: "stream-group__chip-day", children: "Pinned" }) })) : dayHeader ? (_jsxs("div", { className: "stream-group__chip", children: [_jsx("span", { className: "stream-group__chip-day", children: dayHeader.day }), _jsx("span", { className: "stream-group__chip-date", children: dayHeader.date })] })) : null, !isPinnedSection && !isFlatSection ? (_jsx("button", { type: "button", className: "stream-group__collapse-btn", onClick: onToggleCollapse, children: isCollapsed ? "▼" : "▲" })) : null] }), _jsxs("div", { ref: setCardsElCombined, className: `stream-group__cards ${isCollapsed ? "stream-group__cards--collapsed" : ""}`, children: [sortableLayerEnabled && reconciledBlockIds && scopeKey ? (_jsx(SortableContext, { id: scopeKey, 
                        // `SortableContext.items` is typed as a mutable array
                        // even though dnd-kit only reads it. Hand it the memoized
                        // reconciled array directly so its shallow equality checks
                        // can short-circuit between unchanged renders.
                        items: reconciledBlockIds, 
                        // No-op strategy: drag is no longer a reorder gesture,
                        // so non-dragging neighbors stay perfectly still. The
                        // dragged block lifts and follows the pointer; the
                        // block currently intersecting it paints a stack-drop
                        // glow via `SortableBlockViewHost`'s `isOver` (gated to
                        // surfaces where stack-create-on-drop is enabled).
                        strategy: NO_OP_SORTING_STRATEGY, disabled: hasOpenStackHere && (unfurlPhase === "opening" || unfurlPhase === "closing"), children: renderedChildren })) : (renderedChildren), lingerStackId !== undefined && cardsEl !== null ? (_jsx(StackFrame, { stackId: lingerStackId, paletteId: lingerPaletteId, cardsRoot: cardsEl, 
                        // Visible during `opening` + `open`. Becomes false at
                        // `closing` so the CSS exit transition fires. The frame
                        // stays mounted (via `lingerStackId`) for
                        // STACK_FRAME_EXIT_LINGER_MS after the stack closes so
                        // the exit animation runs to completion even after
                        // `openStackId` has been cleared and the unfurl items
                        // have already unmounted.
                        isVisible: hasOpenStackHere && (unfurlPhase === "opening" || unfurlPhase === "open"), 
                        // Gated on `hasOpenStackHere` so this group's frame only
                        // reacts to count changes when the unfurled stack is the
                        // one in this group — other groups' stack changes
                        // shouldn't churn this frame's observer set. Passing `0`
                        // during the linger window (post-close, pre-unmount)
                        // intentionally re-runs the layout effect, but its body
                        // bails on `!isVisible`, so the observer teardown that
                        // already happened on the `open → closing` transition
                        // stays torn down.
                        unfurlItemCount: hasOpenStackHere && unfurlEnvelopes !== undefined ? unfurlEnvelopes.length : 0 })) : null] })] }));
});
StreamGroup.displayName = "StreamGroup";
// ---------------------------------------------------------------------------
// StreamLoadMore (internal)
// ---------------------------------------------------------------------------
// Distance (in pixels, beyond the viewport bottom) at which we start
// fetching the next page. Set very large so the next page is almost
// always already in cache by the time the user scrolls within sight of
// the bottom — the user should never see a "Load more" affordance
// because the request fires roughly a viewport-and-a-half early on
// most laptops, and several screens early on phones. Tune down if this
// proves too chatty against the API in testing.
const LOAD_MORE_PREFETCH_PX = 5000;
const StreamLoadMore = ({ hasMore, isFetchingNextPage, onFetchNextPage, }) => {
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore || isFetchingNextPage)
            return;
        const el = sentinelRef.current;
        if (!el)
            return;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting))
                onFetchNextPage();
        }, { rootMargin: `${LOAD_MORE_PREFETCH_PX}px 0px`, threshold: 0 });
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, isFetchingNextPage, onFetchNextPage]);
    if (!hasMore) {
        return _jsx("div", { className: "stream__end-spacer", "aria-hidden": true });
    }
    // No visible button: prefetch fires `LOAD_MORE_PREFETCH_PX` before
    // the sentinel reaches view, so by the time the user scrolls past
    // the previous batch the next page is already streaming in. We keep
    // the sentinel mounted so the IntersectionObserver has a target, and
    // surface a low-profile spinner only while a fetch is actually in
    // flight (slow networks, very fast scrolling) so the bottom of the
    // feed doesn't look frozen.
    return (_jsx("div", { ref: sentinelRef, className: "stream__load-more", children: isFetchingNextPage ? (_jsx("div", { className: "stream__load-more-indicator", role: "status", "aria-live": "polite", children: _jsx("span", { className: "spinner", "aria-hidden": true }) })) : null }));
};
// ---------------------------------------------------------------------------
// Stream
// ---------------------------------------------------------------------------
const PINNED_SECTION_KEY = "__pinned__";
// Drag activation thresholds, split by input device.
//
// Mouse — distance-based. The cursor must travel `distance` px from
// the mousedown coords before dnd-kit owns the pointer stream; a
// press that doesn't move that far bubbles as a normal click and
// reaches the card's `onClick` (tap-to-open). 1px is the minimum
// value that still lets `onClick` fire: `distance: 0` would
// activate on `mousedown` itself and swallow every click. Desktop
// has no scroll-vs-drag conflict so 1px feels instantaneous.
const REORDER_MOUSE_ACTIVATION_DISTANCE = 1;
// Touch — strict stationary hold. Distance-only thresholds on touch
// are unsafe: a fast vertical swipe-intended-as-scroll IS just
// vertical movement past any small distance, indistinguishable from
// a deliberate drag by distance alone, so dnd-kit captured swipes
// as drag-starts while the browser simultaneously tried to scroll
// — finger owned by two systems, page felt "stuck."
//
// An earlier iteration of this gesture used `delay: 200ms` with
// `tolerance: 8px` — which still hijacked slow scrolls: a finger
// drifting 5–7px during the first 200ms stayed inside the 8px
// tolerance window and activated a drag at 200ms anyway. The fix
// is two-fold:
//
//   - Use a 300ms hold for drag pickup. Long enough that scroll
//     intent (which starts moving well before this) cancels
//     reliably; short enough that the lift feels responsive.
//   - Drop tolerance to 2px (pure finger jitter only). Anything
//     past 2px during the hold cancels activation cleanly, so
//     the browser owns native scroll. Fingers honestly only
//     jitter 0–1px on a deliberate hold.
//
// `useLongPress`'s touch threshold (`TOUCH_LONG_PRESS_DELAY_MS`,
// currently 650ms) is kept 350ms past this delay so a stationary
// hold can still escalate from "drag pickup" (300ms) into
// "long-press multi-select" (650ms). See use-long-press.ts.
const REORDER_TOUCH_ACTIVATION_DELAY_MS = 300;
const REORDER_TOUCH_ACTIVATION_TOLERANCE_PX = 2;
// Drag-rescue threshold for exiting multi-select. dnd-kit's per-input
// activation thresholds above (1px mouse, 2px touch tolerance) own
// the "drag has started" moment. Once we're already in multi-select
// mode, though, tiny post-long-press finger drift shouldn't yank the
// user out of the selection chrome — only a deliberate drag should.
//
// `handleDragMove` checks `event.delta` (total translation since drag
// activation) against this distance and exits multi-select only past
// the threshold. The card still gets dnd-kit's lift + halo visual at
// 1–2px so the gesture feels alive, but the selection chrome stays
// put for short movements. Pick a value comfortably larger than
// typical finger jitter and the sensor activation distances; 24px is
// roughly half a touch-target side, well past honest jitter, and
// short enough that committing to a real drag still feels immediate.
const MULTISELECT_EXIT_DRAG_DISTANCE_PX = 24;
// Sentinel prefix for the Pinned group's `<SortableContext>` id.
// Kept distinct so `Stream.handleDragEnd` can recognize Pinned-section
// drops if/when a Pinned-only reorder gesture is reintroduced. Drag
// no longer writes any reorder (regular or pinned) — the gesture is
// stack-only.
const PINNED_SORTABLE_SCOPE_PREFIX = "pinned:";
// No-op sorting strategy. `SortableContext` requires a `strategy`
// callback to compute how non-dragging items shift to "make room" for
// the dragged item — but reorder is no longer a thing on this surface,
// so we deliberately return `null` for every neighbor: their
// `useSortable.transform` falls to `null`, no inline transform paints,
// no flex-row reflow happens. Only the actively-dragged item moves
// (its transform comes from the drag's own `useSortable.transform`
// path with the live-drag-transform fallback for non-sortable hover
// targets — see `SortableBlockViewHost`).
//
// Module-scope so identity stays stable across renders; dnd-kit
// recomputes layout when the strategy reference changes, and we don't
// want a fresh closure per Stream render to thrash that.
const NO_OP_SORTING_STRATEGY = () => null;
// dnd-kit's `autoScroll` defaults to a 20% threshold on BOTH axes —
// it walks up the DOM tree looking for scrollable ancestors and
// auto-scrolls any of them whose edge the pointer comes within 20%
// of. Stream surfaces are exclusively vertical: `<ScrollView>` is
// configured with `axis: "y"` (overflow-x: hidden) and every
// ancestor up to `#root` is `overflow: hidden`. We never want the
// drag to nudge the page sideways — only vertical autoscroll so
// the user can pull a card toward an off-screen day group. Set
// `threshold.x = 0` to disable horizontal autoscroll outright; the
// trigger condition becomes `pointer.x >= container.right` which
// effectively never fires inside our viewport-bound layout. Module
// scope keeps the object identity stable across renders so
// dnd-kit doesn't re-initialise its autoscroll machinery on every
// pointer move.
const DND_AUTOSCROLL_OPTIONS = { threshold: { x: 0, y: 0.2 } };
// Lazy haptics singleton, dedicated to the drag-pickup feedback
// pulse. Separate from the long-press primitive's instance so the
// two gestures' feedback channels stay independent.
let dragHaptics;
function getDragHaptics() {
    if (!dragHaptics)
        dragHaptics = new WebHaptics();
    return dragHaptics;
}
function isTouchActivatorEvent(event) {
    // Desktop Safari can omit the global TouchEvent constructor entirely.
    // Guard it before instanceof so mouse drag-starts do not throw.
    return typeof TouchEvent !== "undefined" && event instanceof TouchEvent;
}
/**
 * Owns the "jump to date" loading spinner AND the scroll-anchor
 * effect. Extracted from `<Stream>` so the `mainStreamStore.useJumping()`
 * subscription lives here, not in Stream itself — `isJumping` flipping
 * doesn't bounce through Stream's 2300-line render. Stream stays
 * stable across jump-completion cycles; only this small leaf re-runs.
 *
 * The effect re-fires whenever the `isJumping` subscription flips OR
 * `scrollAnchor` resolves (Stream computes the anchor from
 * `activeJumpTarget` + `dateKeys` + `feed.blocks`). The
 * `lastScrolledJumpToken` ref dedupes consecutive scroll attempts at
 * the same target so the page doesn't re-scroll on every re-render of
 * this leaf — once a target is scrolled into view, the same activeJumpTarget+dateKey
 * combination won't trigger another `scrollIntoView` until the user
 * picks a new target.
 */
const StreamJumpToHandler = memo(({ activeJumpTarget, scrollAnchor }) => {
    const isJumping = mainStreamStore.useJumping();
    const lastScrolledJumpToken = useRef(undefined);
    useEffect(() => {
        if (!activeJumpTarget) {
            lastScrolledJumpToken.current = undefined;
            return;
        }
        if (isJumping || !scrollAnchor.domId || !scrollAnchor.dateKey)
            return;
        const token = `${activeJumpTarget}\0${scrollAnchor.dateKey}`;
        if (lastScrolledJumpToken.current === token)
            return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = document.getElementById(scrollAnchor.domId);
                if (!el)
                    return;
                lastScrolledJumpToken.current = token;
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        });
    }, [isJumping, activeJumpTarget, scrollAnchor.domId, scrollAnchor.dateKey]);
    if (!isJumping)
        return null;
    return (_jsx("div", { className: "stream__jump-loading", role: "status", "aria-live": "polite", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "stream__jump-loading-spinner" }) }));
});
StreamJumpToHandler.displayName = "StreamJumpToHandler";
export const Stream = memo(({ data, columns = 2, groupBy = "day", pinnedItems: _externalPinnedItems, showGaps = false, activeJumpTarget, typeFilter, onOpenCollection, surfaceKey, pinScope, pinSurface, pinSlotLabel, enablePinnedSection, createStackOnDropParentId, groupSameCollectionPerDay, darkStackTiles, analyticsSurface, footer, }) => {
    // PouchApp handle for the drag-rescue path in `handleDragMove`:
    // if the user moves the finger after `useLongPress` has fired
    // multi-select (~800ms hold), the in-flight drag wins and we
    // exit multi-select so the two affordances don't fight for the
    // pointer.
    const app = useApp();
    const segments = mainStreamStore.useSegments();
    // The Stream reads its pinned section from `(pinScope, pinSurface)`
    // when explicitly provided, falling back to the homepage feed slot
    // for the legacy account-scope call sites that don't pass pin
    // props. The homepage's `pinnedStreamStore` is a thin wrapper
    // around `usePinList({type: "account"}, "feed")` plus warm/sync
    // helpers — its `useOrder()` returns the same data as
    // `usePinList(...)` so the fallback is just convention, not a
    // behavioural fork.
    const explicitPinSlot = pinScope !== undefined;
    // Memoize the resolved scope so child hook deps and effect deps
    // see stable references across renders. Parents typically pass an
    // inline object literal (`pinScope={{type: "collection", id}}`),
    // so we key on the structural fields rather than the prop's
    // reference identity.
    const pinScopeType = pinScope?.type;
    const pinScopeId = pinScope?.type === "collection" ? pinScope.id : undefined;
    const effectivePinScope = useMemo(() => {
        if (pinScopeType === "collection" && pinScopeId !== undefined) {
            return { type: "collection", id: pinScopeId };
        }
        return { type: "account" };
    }, [pinScopeType, pinScopeId]);
    const effectivePinSurface = pinSurface ?? "feed";
    const homepagePins = pinnedStreamStore.useOrder();
    const explicitPins = usePinList(effectivePinScope, effectivePinSurface);
    const pinOrder = explicitPinSlot ? explicitPins : homepagePins;
    // Hydrate explicit pin slots (Group / Collection / Stack) from the
    // server on mount. `usePinList` only reads the localStorage-backed
    // snapshot, so without this a collection's pinned top row stays empty
    // on any device/browser where the pins weren't toggled locally. The
    // homepage feed slot is intentionally excluded — it's bootstrapped
    // via `pinnedStreamStore.warm()` (which also prefetches envelopes) on
    // app boot, and the Stream reads it through `homepagePins` above.
    useEffect(() => {
        if (explicitPinSlot === false)
            return;
        void bootstrapPinsFromApi(effectivePinScope, effectivePinSurface);
    }, [explicitPinSlot, effectivePinScope, effectivePinSurface]);
    // PinSlotContext is published only when the caller explicitly opts
    // in. Homepage Streams that fall back to `(account, feed)` don't
    // publish, so any future card on the homepage keeps its own
    // destination-picker UX instead of becoming a feed-pin toggle.
    const pinSlotContextValue = useMemo(() => {
        if (explicitPinSlot === false)
            return undefined;
        return {
            scope: effectivePinScope,
            surface: effectivePinSurface,
            label: pinSlotLabel ?? "this view",
        };
    }, [explicitPinSlot, effectivePinScope, effectivePinSurface, pinSlotLabel]);
    // -- Same-collection-per-day grouping (synthetic stacks) ----------------
    //
    // When opted in, the Stream collapses runs of 2+ blocks that share
    // the same non-primary `collectionId` within a single day into a
    // single visual "Stack" tile. The grouping is post-strategy and
    // visual-only — no server round-trip, no stored Stack collection.
    // Currently used by the homepage feed to declutter days with many
    // captures into the same Collection (e.g. "What is Putty?").
    //
    // Subscribe to the cross-layer collection cache so we can resolve
    // each block's `collectionId` to its real `CollectionItem` (for
    // title, palette, type-check). `useListAllForSlugLookup` returns
    // the raw cache — every layer in one shot — which is exactly what
    // we need; a block can sit in a layer-0 Group or layer-1 Collection
    // and either should be groupable. Hook fires on every Stream mount
    // for a stable hook order; the lookup function downgrades to
    // `() => undefined` when the surface hasn't opted in, so the
    // grouping step becomes a no-op without unmounting the hook.
    const allCollectionsForLookup = collectionStore.useListAllForSlugLookup();
    const lookupCollection = useMemo(() => {
        if (groupSameCollectionPerDay !== true) {
            return () => undefined;
        }
        const byId = new Map();
        for (const c of allCollectionsForLookup.collections)
            byId.set(c.id, c);
        return (id) => byId.get(id);
    }, [groupSameCollectionPerDay, allCollectionsForLookup.collections]);
    const { items, collections, isLoading, hasMore, isFetchingNextPage, fetchMore } = data;
    // Pinned section renders when an explicit pinScope is configured
    // (Group / Collection detail) OR when the homepage opts in via
    // `enablePinnedSection`. All other surfaces opt out — e.g. smart
    // collections, which never want a pinned strip above the results.
    const pinningEnabled = explicitPinSlot || enablePinnedSection === true;
    // -- Collapse state (owned by Stream) ------------------------------------
    const [collapsedSections, setCollapsedSections] = useState(new Set());
    const toggleSection = useCallback((dateKey) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev);
            if (next.has(dateKey)) {
                next.delete(dateKey);
            }
            else {
                next.add(dateKey);
            }
            return next;
        });
    }, []);
    // Stable per-key toggle handler so React.memo on StreamGroup isn't
    // defeated by a fresh inline closure each render.
    const toggleHandlers = useRef(new Map());
    const getToggleHandler = useCallback((key) => {
        let fn = toggleHandlers.current.get(key);
        if (!fn) {
            fn = () => toggleSection(key);
            toggleHandlers.current.set(key, fn);
        }
        return fn;
    }, [toggleSection]);
    // -- Stack overlay -------------------------------------------------------
    // Stacks (layer-2 Collections) sort by their newest member's
    // `surfaceAt` instead of the raw `lastAddedAt` — see
    // `useStackNewestMemberAt` for the rationale. The hook returns a
    // map of stackId -> isoString; we overlay each entry as a synthetic
    // `lastAddedAt` so the existing `collectionActivityTimestamp` (and
    // therefore `buildFeedItems` and the day-group sort) flow through
    // unchanged. Layer-1 Collections are passed through untouched.
    //
    // The overlay runs BEFORE pin filtering so that pinned Stacks
    // emitted into the Pinned section also get the corrected
    // timestamp (the section uses `sortAt`).
    const stackNewestMemberAt = useStackNewestMemberAt(collections);
    const augmentedCollections = useMemo(() => {
        if (collections === undefined)
            return collections;
        if (stackNewestMemberAt.size === 0)
            return collections;
        return collections.map((c) => {
            if (c.layerIndex !== COLLECTION_LAYER.stack)
                return c;
            const newest = stackNewestMemberAt.get(c.id);
            if (newest === undefined)
                return c;
            // Floor the content-anchored overlay at the stack's own
            // `createdAt`: a stack created today from blocks captured
            // weeks ago should still anchor to today (where the user
            // organized it) rather than sliding down to the content's
            // date and visually "disappearing" from the top of the
            // feed. Once a member newer than `createdAt` lands inside
            // (a fresh capture, a recent block moved in), the
            // content-anchored sort takes precedence again.
            //
            // ISO timestamps compare correctly lexicographically.
            const effective = newest.localeCompare(c.createdAt) > 0 ? newest : c.createdAt;
            return { ...c, lastAddedAt: effective };
        });
    }, [collections, stackNewestMemberAt]);
    // -- Pin filtering -------------------------------------------------------
    const feed = useMemo(() => pinningEnabled
        ? excludePinnedFromItems(items, augmentedCollections, pinOrder)
        : { blocks: items, collections: augmentedCollections }, [pinningEnabled, items, augmentedCollections, pinOrder]);
    const pinnedItems = useMemo(() => (pinningEnabled ? orderedPinnedFeedItems(pinOrder, items, augmentedCollections) : []), [pinningEnabled, items, augmentedCollections, pinOrder]);
    // -- Grouping ------------------------------------------------------------
    const strategy = useMemo(() => resolveGroupStrategy(groupBy), [groupBy]);
    const feedItems = useMemo(() => buildFeedItems(feed.blocks, feed.collections), [feed.blocks, feed.collections]);
    const rawGroups = useMemo(() => {
        if (feedItems.length === 0)
            return [];
        return strategy(feedItems);
    }, [strategy, feedItems]);
    // Apply same-collection-per-day merging if the surface opts in.
    // Skipped (returns `rawGroups` unchanged) when the flag is off, so
    // every other Stream surface keeps its pre-existing behavior.
    const groups = useMemo(() => {
        if (groupSameCollectionPerDay !== true)
            return rawGroups;
        return mergeSameCollectionBlocks(rawGroups, lookupCollection);
    }, [rawGroups, groupSameCollectionPerDay, lookupCollection]);
    // Index synthetic stacks emitted by the merge: id → block envelopes.
    // Feeds `useStackUnfurl`'s envelope-override path so the unfurl pulls
    // the synthetic's pre-bundled blocks instead of round-tripping for a
    // server-side stack's children (synthetic ids have no server row).
    // Falls back to empty when the merge didn't introduce any synthetic
    // stacks, which is the common case.
    const syntheticEnvelopeOverrides = useMemo(() => {
        const overrides = new Map();
        for (const group of groups) {
            for (const item of group.items) {
                if (item.kind === "synthetic-stack") {
                    overrides.set(item.id, item.blocks);
                }
            }
        }
        return overrides;
    }, [groups]);
    // -- Stack unfurl --------------------------------------------------------
    // The hook owns the open-stack state machine + both FLIP transitions.
    // Stacks can sit in any group and unfurling pushes cards across every
    // group below, so the state has to live above all groups. Synthetic
    // stacks bypass the children query via `envelopeOverrides` — their
    // ids don't exist server-side.
    const dateKeys = useMemo(() => groups.map((g) => g.key), [groups]);
    // -- Pinned group --------------------------------------------------------
    const pinnedGroup = useMemo(() => {
        if (pinnedItems.length === 0)
            return undefined;
        return { key: PINNED_SECTION_KEY, label: "Pinned", items: pinnedItems };
    }, [pinnedItems]);
    // -- Jump-to-date --------------------------------------------------------
    const scrollAnchor = useMemo(() => {
        const range = getScrollRangeForTarget(activeJumpTarget);
        if (!range || !activeJumpTarget)
            return { domId: undefined, dateKey: undefined };
        const firstKey = firstDateKeyOverlappingRange(dateKeys, feed.blocks, range);
        if (!firstKey)
            return { domId: undefined, dateKey: undefined };
        return { domId: jumpTargetDomId(activeJumpTarget), dateKey: firstKey };
    }, [activeJumpTarget, dateKeys, feed.blocks]);
    // -- Gap pairs -----------------------------------------------------------
    const gapPairs = useMemo(() => {
        if (!showGaps || segments.length < 2)
            return [];
        return buildGapPairs(segments);
    }, [showGaps, segments]);
    // -- Render --------------------------------------------------------------
    // Chronological groupings (day/week/month) emit a date header per
    // section, so the per-card chip drills down to the time within the
    // day rather than repeating the date — the user gets both halves of
    // the timestamp without duplication. Flat layouts (`groupBy="none"`
    // — smart collections, the collection grid view) mix captures across
    // days, so the chip carries the date instead. The Pinned section
    // overrides this with `"date"` directly at its callsite below.
    const cardDateChipMode = groupBy === "none" ? "date" : "time";
    // -- Reorder wiring ------------------------------------------------------
    // Scope-key helper for non-pinned groups. The Pinned group uses a
    // distinct `pinned:*` prefix so `onDragEnd` can route it separately.
    const buildScopeKeyFor = useCallback((groupKey) => {
        if (!surfaceKey)
            return undefined;
        return buildManualOrderScopeKey(surfaceKey, groupKey);
    }, [surfaceKey]);
    const pinnedScopeKey = surfaceKey ? `${PINNED_SORTABLE_SCOPE_PREFIX}${surfaceKey}` : undefined;
    // Sensors live at the Stream level and are stable across renders.
    // Mouse + touch are split so each input device gets its own
    // activation pattern (see the constants above):
    //
    //   - Mouse: distance-based. Movement past N px owns the pointer.
    //   - Touch: delay-based. Hold N ms with ≤ tolerance px finger
    //     jitter owns the pointer; movement before the hold
    //     completes falls through to native scroll.
    //
    // Pure clicks / quick taps reach the card's `onClick` unchanged
    // either way (mouse: no movement; touch: released inside the
    // tap-eligible window before the delay completes).
    // `KeyboardSensor` + `sortableKeyboardCoordinates` wires arrow-key
    // reorder for accessibility.
    const sensors = useSensors(useSensor(MouseSensor, {
        activationConstraint: { distance: REORDER_MOUSE_ACTIVATION_DISTANCE },
    }), useSensor(TouchSensor, {
        activationConstraint: {
            delay: REORDER_TOUCH_ACTIVATION_DELAY_MS,
            tolerance: REORDER_TOUCH_ACTIVATION_TOLERANCE_PX,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    // Layered collision detection:
    //
    //   1. Container droppables (stack tiles + unfurled stack-member
    //      cards) resolved via overlap with the dragged card — a stack
    //      only glows when it is physically beneath the held block, and
    //      that beats any sibling-block candidate. Stacks (both the
    //      closed-state tile AND each open-state unfurled card) identify
    //      themselves with `data.current.type === "stack"`. Same-stack
    //      droppables remain in the candidate set for stack-member drags
    //      so a short release back inside the source frame resolves to
    //      `over` (and the `handleDragEnd` `sourceStackId ===
    //      targetStackId` guard no-ops it) rather than falling through
    //      to "move out".
    //   2. Stack-member drags get an early bail: if step 1 didn't hit,
    //      we return `[]` so `over` is `null` in `handleDragEnd`. The
    //      Stream interprets "stack-member released with no over" as
    //      "drop the block out of its source stack into the parent
    //      Collection."
    //   3. Sortable block droppables resolved via overlap when the
    //      surface can create stacks on drop. Nearest-center fallback is
    //      intentionally NOT used there: releasing in empty grid space
    //      should be a no-op, not "choose the nearest card / stack."
    //      The candidate set depends on the surface:
    //        - `createStackOnDropParentId` is set (layer-1 Collection
    //          detail, Group view, or homepage Stream → Primary):
    //          include EVERY sortable block on the surface so a block
    //          from Day A can be dragged onto a block in Day B and
    //          resolve cleanly. The drop creates a Stack containing
    //          both, parented to whichever Collection or Group the
    //          surface points at.
    //        - Otherwise (smart collection, inside-stack): restrict to
    //          the same `SortableContext` as the source. `handleDragEnd`
    //          no-ops on block targets here, so this is purely a
    //          perf/visual restriction — glow + over-state stay scoped
    //          instead of fanning out to neighbors that wouldn't accept
    //          the drop anyway.
    const collisionDetectionStrategy = useCallback((args) => {
        const activeData = args.active.data.current;
        const isStackMember = activeData?.type === "stack-member";
        const containerDroppables = args.droppableContainers.filter((c) => {
            const d = c.data.current;
            if (d?.type === "stack")
                return true;
            return false;
        });
        if (containerDroppables.length > 0) {
            // Stack-member drags use pointerWithin so just moving the pointer
            // outside the frame is enough to "escape" — the entire card rect
            // doesn't need to physically clear every sibling droppable.
            // Regular block drags use rectIntersection so the drop-into-stack
            // glow only fires when the card physically overlaps the tile.
            const containerHits = (isStackMember ? pointerWithin : rectIntersection)({
                ...args,
                droppableContainers: containerDroppables,
            });
            if (containerHits.length > 0)
                return containerHits;
        }
        if (isStackMember)
            return [];
        const sortableDroppables = args.droppableContainers.filter((c) => c.data.current?.sortable !== undefined);
        if (createStackOnDropParentId !== undefined) {
            return rectIntersection({ ...args, droppableContainers: sortableDroppables });
        }
        const sourceContainer = activeData?.sortable?.containerId;
        if (typeof sourceContainer !== "string")
            return closestCenter(args);
        const sameScope = sortableDroppables.filter((c) => c.data.current?.sortable?.containerId === sourceContainer);
        return closestCenter({ ...args, droppableContainers: sameScope });
    }, [createStackOnDropParentId]);
    // Per-scope element map for the foreign-scope pointer probe. Each
    // `<StreamGroup>` registers its `.stream-group__cards` element here
    // via `setCardsElCombined`. The probe in `handleDragMove` walks
    // this map's rects and checks if the live pointer is inside a
    // scope OTHER than the source's.
    //
    // `useRef<Map>` rather than `useState<Map>` because rebuilding the
    // map on every group mount/unmount would churn renders downstream
    // without changing the actual element identities.
    const scopeElementsRef = useRef(new Map());
    const registerScopeElement = useCallback((key, el) => {
        if (el === null) {
            scopeElementsRef.current.delete(key);
        }
        else {
            scopeElementsRef.current.set(key, el);
        }
    }, []);
    // Drag-lifecycle state — two scope keys driving every derived
    // signal in the shared `state.drag` slice on `PouchApp`:
    //   - `activeSourceScopeKey`: source of the in-flight drag (set
    //     on `dragStart`, cleared on `dragEnd`/`dragCancel`). Drives
    //     `data-active-drag-source` → CSS lifts the source section
    //     above sibling sections so the dragged card paints above
    //     foreign-day cards.
    //   - `foreignPointerScopeKey`: scope the pointer is currently
    //     inside if foreign (set on `dragMove`, cleared on
    //     `dragEnd`/`dragCancel`). Drives `data-drag-disabled` →
    //     CSS dims that section's cards. Also drives the dragged
    //     card's own `data-foreign-scope` cursor + dim.
    //
    // The entire drag lifecycle (source/foreign scope keys, live
    // pointer-tracking transform fallback, multi-drag deck) lives in
    // `PouchApp` as `state.drag`. `<Stream>` doesn't own any drag
    // useState — owning state here would force `<Stream>` to re-render
    // 60×/sec on every drag move, re-running its ~2300-line jsx() body
    // and forcing every memoized child to do a fresh props comparison.
    // Pushing the state into Zustand lets consumers subscribe via
    // narrow `app.useStore(...)` selectors that bail out via Object.is
    // when their slice didn't change — non-participating blocks pay
    // zero render cost for per-move updates. See `PouchApp.setDragXxx`
    // for the writer-side contract.
    // Latest pointer position from the activator event (mousedown /
    // touchstart). dnd-kit's `DragMoveEvent` doesn't carry pointer
    // coordinates directly — we have to grab them from the underlying
    // browser event. Tracked in a ref to avoid an extra render per
    // pointer movement; `handleDragMove` reads it synchronously.
    const lastPointerRef = useRef(null);
    // Pointer move listener mounted at the document level so we keep
    // up-to-date coords for the foreign-scope hit-test regardless of
    // whether a drag is in flight. Mounts only when this surface
    // participates in dnd-kit reorder (i.e. when `surfaceKey` is set,
    // which is the same condition that mounts the `<DndContext>`
    // wrapper below). Non-reorderable surfaces (the layer-1
    // Collections grid) pay zero listener cost.
    useEffect(() => {
        if (!surfaceKey)
            return;
        const onPointerMove = (e) => {
            lastPointerRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("pointermove", onPointerMove, { passive: true });
        return () => {
            window.removeEventListener("pointermove", onPointerMove);
        };
    }, [surfaceKey]);
    const handleDragStart = useCallback((event) => {
        const activeId = String(event.active.id);
        const sourceContainerId = event.active.data.current?.sortable?.containerId;
        const sourceScopeKey = typeof sourceContainerId === "string" ? sourceContainerId : undefined;
        // Multi-drag arming. When the user is in block multiselect with
        // ≥ 2 blocks selected AND picked up one of them, the other
        // selected blocks become "companions" that visually ride along
        // behind the leader and join the drop payload on release. We
        // capture every companion's initial DOM rect here (one
        // `getBoundingClientRect()` per selected block) so the companion
        // hosts can compute a stable delta to the leader without having
        // to measure their own rect mid-render. Single layout read
        // batched at drag start: cheaper than per-companion per-move
        // measurement, and stale-rect risk is bounded by drag duration
        // (no layout reflows happen during a drag).
        //
        // We ONLY arm multi-drag when the leader is itself a selected
        // block. Dragging a NON-selected card while in multiselect mode
        // falls through to the existing drag-rescue path (handleDragMove
        // exits multiselect on movement past the threshold), giving the
        // user single-card drag semantics on the just-picked-up card —
        // the same affordance non-multiselect drag offers.
        let multi;
        const selection = app.state.selection;
        if (app.state.ui.isMultiselectMode &&
            selection.size >= 2 &&
            selection.has(activeId)) {
            // dnd-kit's `event.active.rect.current.initial` is `null` at
            // `onDragStart` — the active rect is measured asynchronously,
            // so it's not populated until after this handler returns
            // (see clauderic/dnd-kit#832). Querying the leader's DOM node
            // directly bypasses that lifecycle gap; we're already doing
            // the same DOM lookup for every companion in the loop below,
            // so the leader read is a single extra `getBoundingClientRect`.
            const activeEl = document.querySelector(`[data-sortable-block-id="${activeId}"]`);
            const activeRect = activeEl?.getBoundingClientRect() ?? null;
            const companions = new Map();
            // Stable rank assignment for the deck order. Selection sets
            // preserve insertion order, but we want a deterministic visual
            // (top-of-deck → bottom) that doesn't depend on whatever order
            // the user happened to tap things in. DOM-document order
            // matches how the selection reads visually on the surface,
            // so we sort companion ids by where their `[data-block-id]`
            // node sits in the document.
            const docNodes = Array.from(document.querySelectorAll("[data-sortable-block-id]"));
            const docOrder = new Map();
            docNodes.forEach((el, idx) => {
                const id = el.dataset.sortableBlockId;
                if (typeof id === "string")
                    docOrder.set(id, idx);
            });
            const companionIds = Array.from(selection)
                .filter((id) => id !== activeId)
                .sort((a, b) => (docOrder.get(a) ?? 0) - (docOrder.get(b) ?? 0));
            for (let i = 0; i < companionIds.length; i++) {
                const id = companionIds[i];
                if (id === undefined)
                    continue;
                const el = document.querySelector(`[data-sortable-block-id="${id}"]`);
                if (el === null)
                    continue;
                const rect = el.getBoundingClientRect();
                companions.set(id, {
                    rank: i,
                    initialLeft: rect.left,
                    initialTop: rect.top,
                });
            }
            if (companions.size > 0 && activeRect !== null) {
                multi = {
                    activeInitialLeft: activeRect.left,
                    activeInitialTop: activeRect.top,
                    companions,
                    selectedBlockIds: Array.from(selection),
                };
            }
        }
        // Single synchronous store write. Zustand's setState lands
        // immediately, so any `onDragMove` dnd-kit fires in the same
        // task already sees the armed lifecycle when it reads
        // `app.drag` — no need for the ref-mirror dance the previous
        // useState-backed implementation required.
        app.setDragStart({
            activeBlockId: activeId,
            sourceScopeKey,
            stackOnDropParentId: createStackOnDropParentId,
            multi,
        });
        // Body-level drag flag. Imperative DOM mutation rather than a
        // React-driven className — chrome that needs to react to drag
        // state (e.g. nav button tooltips that re-render on every
        // pointer-move when armed) reads this via a `body[data-drag-in-flight] …`
        // CSS selector and can apply `pointer-events: none` to suppress
        // hover handlers during the drag without going through React at
        // all. Cleared in `handleDragEnd` / `handleDragCancel` below.
        document.body.setAttribute("data-drag-in-flight", "");
        // Touch lifts get a haptic so the "you've picked it up" state
        // change reads clearly on mobile alongside the wrapper's halo
        // + scale visual. Mouse drags stay silent — desktop has no
        // haptic surface and the visual treatment is enough.
        // `activatorEvent` is the underlying browser event that
        // crossed the sensor's activation constraint, so its type is
        // a reliable input-modality discriminator for this single drag
        // lifecycle.
        if (isTouchActivatorEvent(event.activatorEvent)) {
            getDragHaptics().trigger("medium");
        }
    }, [app, createStackOnDropParentId]);
    const handleDragMove = useCallback((event) => {
        const { x: dx, y: dy } = event.delta;
        // Drag rescue: drag motion wins over multi-select. If the user
        // held long enough for `useLongPress` to fire multi-select but
        // then dragged the finger past `MULTISELECT_EXIT_DRAG_DISTANCE_PX`,
        // they meant to drag — exit multi-select so the in-flight
        // gesture isn't fighting the selection chrome. The threshold is
        // intentionally larger than dnd-kit's sensor activation
        // distances so small post-long-press drift doesn't qualify as a
        // rescue (see the constant for rationale). Squared distance to
        // avoid a sqrt on every move. Idempotent past the threshold
        // (no-op once multi-select is already off), so re-checking on
        // every dragMove is safe.
        //
        // Taps in multi-select still toggle selection cleanly because
        // a tap doesn't generate `onDragMove` events.
        //
        // SKIPPED for multi-drag: if `handleDragStart` armed multi-drag
        // (drag began on a selected card while ≥ 2 were selected), the
        // user explicitly wants the whole selection to ride along. The
        // companion deck animation IS the chrome here, and exiting
        // multi-select would vaporise the selection mid-gesture.
        if (app.state.ui.isMultiselectMode &&
            app.drag.multi === undefined) {
            const distSq = dx * dx + dy * dy;
            if (distSq >= MULTISELECT_EXIT_DRAG_DISTANCE_PX * MULTISELECT_EXIT_DRAG_DISTANCE_PX) {
                app.exitMultiselectMode();
            }
        }
        // Foreign-scope probe — sets `foreignPointerScopeKey` whenever
        // the pointer is inside a `<SortableContext>` other than the
        // source. The CSS reading that key dims the foreign section's
        // cards as a "drop won't land here" affordance.
        //
        // Skipped (foreignKey stays undefined) when stack-create-on-drop
        // is enabled for this surface (`createStackOnDropParentId !==
        // undefined`): on layer-1 Collection surfaces, cross-day drops
        // onto another block DO resolve — they create a Stack
        // containing both blocks. The dim would be actively misleading
        // there, painting "rejected" chrome on a section where the
        // drop will actually succeed. The hovered target block's
        // `--drop-target` glow is the affordance instead.
        let foreignKey;
        const sourceContainer = event.active.data.current?.sortable?.containerId;
        const pointer = lastPointerRef.current;
        if (createStackOnDropParentId === undefined &&
            typeof sourceContainer === "string" &&
            pointer !== null) {
            // Hit-test the pointer against each registered scope's rect.
            // The first non-source scope containing the pointer is the
            // foreign target. `getBoundingClientRect()` is cheap on a
            // small map (typically ≤ a couple dozen day groups visible
            // at once); we only run this per `onDragMove` so it's not on
            // the render hot path.
            for (const [scopeKey, el] of scopeElementsRef.current) {
                if (scopeKey === sourceContainer)
                    continue;
                const rect = el.getBoundingClientRect();
                if (pointer.x >= rect.left &&
                    pointer.x <= rect.right &&
                    pointer.y >= rect.top &&
                    pointer.y <= rect.bottom) {
                    foreignKey = scopeKey;
                    break;
                }
            }
        }
        // Single store write per move. `setDragMove` produces a new
        // `liveTransform` object (the actively-dragged block's selector
        // returns this reference and re-renders) but assigns the
        // `foreignPointerScopeKey` primitive directly — Object.is in
        // field-level selectors handles dedupe automatically, so
        // groups whose scope key didn't transition won't re-render.
        app.setDragMove({
            liveTransform: { x: dx, y: dy },
            foreignPointerScopeKey: foreignKey,
        });
    }, [app, createStackOnDropParentId]);
    const handleDragCancel = useCallback(() => {
        app.setDragEnd();
        document.body.removeAttribute("data-drag-in-flight");
    }, [app]);
    const handleDragEnd = useCallback((event) => {
        // Snapshot the multi-drag payload BEFORE clearing the lifecycle.
        // The drop branches below read this snapshot to decide whether
        // to ship a single-block or a multi-block move/stack-create —
        // a local closure-captured reference survives the `setDragEnd`
        // wipe below because JS closures preserve the object identity,
        // so the `setDragEnd` call only affects future reads of
        // `app.drag`.
        const multiDragSnapshot = app.drag.multi;
        const isMultiDrag = multiDragSnapshot !== undefined;
        app.setDragEnd();
        document.body.removeAttribute("data-drag-in-flight");
        const { active, over } = event;
        const activeData = active.data.current;
        // Stack-member released with no `over` → drop the block out of
        // its source stack into the parent Collection. The collision
        // strategy above returns `[]` for stack-member drags that didn't
        // land on a foreign Stack tile, so this branch fires for the
        // common "drag out into the surrounding grid" gesture.
        //
        // Drops far off-screen also hit this path, but the user can hit
        // Esc mid-drag to cancel — and any server-side failure surfaces
        // a toast via the catch below, with the optimistic move rolled
        // back inside `collectionStore.moveBlocksBetweenParentChild`.
        if (activeData?.type === "stack-member" && over === null) {
            const sourceStackId = typeof activeData.stackId === "string" ? activeData.stackId : undefined;
            const parentCollectionId = typeof activeData.parentCollectionId === "string"
                ? activeData.parentCollectionId
                : undefined;
            if (sourceStackId === undefined || parentCollectionId === undefined)
                return;
            const blockId = String(active.id);
            void collectionStore
                .moveBlocksBetweenParentChild([blockId], sourceStackId, parentCollectionId)
                .catch((e) => {
                showToast.error(e instanceof Error ? e.message : "Failed to remove block from stack");
            });
            return;
        }
        if (!over)
            return;
        const overData = over.data.current;
        // Stack drop branch — moves the dragged block into the target
        // stack via its parent Collection. The collision strategy
        // resolves `over` to the stack tile via physical overlap. Two
        // source shapes feed this path:
        //
        //   - regular block from the parent's grid → single
        //     parent-to-stack move. The source IS the stack's
        //     `parentCollectionId` because that's the only Collection
        //     a grid block in this surface can be in.
        //   - unfurled stack-member from a DIFFERENT stack → two-hop
        //     chain (source stack → parent → target stack) since the
        //     server's `moveBlocksBetweenParentChild` only accepts a
        //     single parent-child relationship. Two sequential awaits
        //     so the second hop sees the first hop's optimistic state.
        //
        // Toast on failure; the optimistic writes inside
        // `collectionStore.moveBlocksBetweenParentChild` roll back on
        // a thrown promise.
        if (overData?.type === "stack") {
            const targetStackId = typeof overData.stackId === "string" ? overData.stackId : undefined;
            const targetParentId = typeof overData.parentCollectionId === "string"
                ? overData.parentCollectionId
                : undefined;
            if (targetStackId === undefined || targetParentId === undefined)
                return;
            const blockId = String(active.id);
            if (activeData?.type === "stack-member") {
                const sourceStackId = typeof activeData.stackId === "string" ? activeData.stackId : undefined;
                if (sourceStackId === undefined)
                    return;
                // Same-stack drops are filtered out at collision time;
                // this is a defensive backstop.
                if (sourceStackId === targetStackId)
                    return;
                void (async () => {
                    try {
                        await collectionStore.moveBlocksBetweenParentChild([blockId], sourceStackId, targetParentId);
                        await collectionStore.moveBlocksBetweenParentChild([blockId], targetParentId, targetStackId);
                    }
                    catch (e) {
                        showToast.error(e instanceof Error ? e.message : "Failed to move block between stacks");
                    }
                })();
                return;
            }
            // Multi-drag: ship every selected block (leader + companions
            // + any virtualized members that didn't get a visible
            // companion rect) into the target stack in a single move
            // call. `selectedBlockIds` is the snapshot taken at drag
            // start; we feed it directly so offscreen members aren't
            // silently dropped from the payload. The multiselect bar
            // exits on success so the user lands back in the normal feed
            // view with the cards now visible inside the target stack.
            if (isMultiDrag) {
                const allBlockIds = multiDragSnapshot.selectedBlockIds.slice();
                if (analyticsSurface !== undefined) {
                    track({
                        name: "blocks.multi_dragged",
                        props: {
                            target: "existing_stack",
                            block_count: allBlockIds.length,
                            surface: analyticsSurface,
                        },
                    });
                }
                void collectionStore
                    .moveBlocksBetweenParentChild(allBlockIds, targetParentId, targetStackId)
                    .then(() => {
                    app.exitMultiselectMode();
                })
                    .catch((e) => {
                    showToast.error(e instanceof Error ? e.message : "Failed to move blocks into stack");
                });
                return;
            }
            void collectionStore
                .moveBlocksBetweenParentChild([blockId], targetParentId, targetStackId)
                .catch((e) => {
                showToast.error(e instanceof Error ? e.message : "Failed to move block into stack");
            });
            return;
        }
        // Block-on-block drop → create a fresh Stack containing both.
        //
        // Gated on `createStackOnDropParentId` being set:
        //   - Layer-1 Collection detail surfaces opt in (parent =
        //     Collection id; see `CollectionDetailTabContent`).
        //   - Group view opts in (parent = Group id; see
        //     `group-view.tsx`).
        //   - Homepage Stream opts in by passing the user's Primary
        //     Group id (`StreamTabContent` in `app-screen.tsx`), so
        //     stacking on the home feed parents the new Stack under
        //     Primary.
        // Smart collections and inside an open Stack stay opted-out —
        // the drag picks up cleanly (so the stack-tile drop paths
        // above still work) but releasing on a regular block does
        // nothing.
        //
        // Stack-member SOURCES never reach this branch — the collision
        // strategy returns `[]` for stack-members that don't land on a
        // stack tile, so they fall through to the `over === null`
        // "remove from stack" branch above. Only regular block drags
        // (no `activeData.type`) can land here.
        //
        // We also skip when the user releases on the SAME block they
        // started dragging (`active.id === over.id`) — that's a
        // long-deliberation tap, not a deliberate "stack with self"
        // gesture.
        if (createStackOnDropParentId !== undefined &&
            activeData?.type === undefined &&
            active.id !== over.id) {
            const sourceBlockId = String(active.id);
            const targetBlockId = String(over.id);
            // Multi-drag: fold the entire selection into the new Stack
            // along with the release-target block. Use the drag-start
            // snapshot of the selection so virtualized members survive;
            // deduplicate so a companion id colliding with the target
            // id doesn't get listed twice. The multiselect bar exits on
            // success — same convention as the "drop on stack tile"
            // branch above.
            if (isMultiDrag) {
                const allBlockIds = Array.from(new Set([targetBlockId, ...multiDragSnapshot.selectedBlockIds]));
                if (analyticsSurface !== undefined) {
                    track({
                        name: "blocks.multi_dragged",
                        props: {
                            target: "new_stack",
                            block_count: allBlockIds.length,
                            surface: analyticsSurface,
                        },
                    });
                }
                track({
                    name: "collection.stack_created",
                    props: { source: "drop_gesture", block_count: allBlockIds.length },
                });
                void createStackFromBlocks(createStackOnDropParentId, allBlockIds)
                    .then(() => {
                    app.exitMultiselectMode();
                })
                    .catch((e) => {
                    showToast.error(e instanceof Error ? e.message : "Failed to create stack");
                });
                return;
            }
            track({
                name: "collection.stack_created",
                props: { source: "drop_gesture", block_count: 2 },
            });
            void createStackFromBlocks(createStackOnDropParentId, [
                sourceBlockId,
                targetBlockId,
            ]).catch((e) => {
                showToast.error(e instanceof Error ? e.message : "Failed to create stack");
            });
            return;
        }
    }, [app, createStackOnDropParentId, analyticsSurface]);
    // Click-outside multi-select exit. A click that lands here on
    // `<ScrollView>` is guaranteed not to have come from a card — every
    // card (block, collection, stack) calls `e.stopPropagation()` on
    // its own `onClick`, so any click that reaches this handler hit
    // the stream's background (between cards, below the last card, on
    // the spacer/load-more sentinel, etc.).
    //
    // The exit only triggers when the active selection is "low
    // investment" (≤ 1 item across all three modes) so a stray click
    // can't accidentally vaporize a multi-item selection the user spent
    // effort assembling. With ≥ 2 selected, an outside click is a no-op
    // — Esc or the Done button is still the explicit way out.
    //
    // State is read imperatively rather than subscribed via `useStore`
    // so the surrounding `<Stream>` doesn't re-render on every
    // selection change; the freshest state at click time is all this
    // handler needs.
    const handleStreamBackgroundClick = useCallback(() => {
        const { ui, selection, collectionSelection, stackSelection } = app.state;
        if (ui.isMultiselectMode === false)
            return;
        const totalSelected = selection.size + collectionSelection.size + stackSelection.size;
        if (totalSelected <= 1) {
            app.exitMultiselectMode();
        }
    }, [app]);
    const streamBody = (_jsxs(ScrollView, { className: `stream stream--cols-${columns}`, endInset: 12, onClick: handleStreamBackgroundClick, children: [_jsx(StackUnfurlCoordinator, { envelopeOverrides: syntheticEnvelopeOverrides }), _jsx(StreamJumpToHandler, { activeJumpTarget: activeJumpTarget, scrollAnchor: scrollAnchor }), pinnedGroup === undefined && groups.length === 0 && hasMore === false ? (_jsx("p", { className: "stream__empty", children: "No blocks to show." })) : null, pinnedGroup ? (_jsx(StreamGroup, { group: pinnedGroup, isCollapsed: false, onToggleCollapse: NOOP, onOpenCollection: onOpenCollection, variant: "pinned", cardDateChipMode: "date", scopeKey: pinnedScopeKey, registerScopeElement: registerScopeElement, darkStackTiles: darkStackTiles }, PINNED_SECTION_KEY)) : null, isLoading && groups.length === 0 ? (_jsx("div", { className: "stream__seed-loading", role: "status", "aria-live": "polite", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "stream__seed-loading-spinner" }) })) : null, groups.flatMap((group, idx) => {
                const nodes = [
                    _jsx(StreamGroup, { group: group, isCollapsed: collapsedSections.has(group.key), onToggleCollapse: getToggleHandler(group.key), onOpenCollection: onOpenCollection, domId: scrollAnchor.dateKey === group.key ? scrollAnchor.domId : undefined, cardDateChipMode: cardDateChipMode, scopeKey: buildScopeKeyFor(group.key), registerScopeElement: registerScopeElement, darkStackTiles: darkStackTiles }, group.key),
                ];
                const nextGroup = groups[idx + 1];
                if (nextGroup && gapPairs.length > 0) {
                    for (const g of gapPairs) {
                        if (dayBelongsToSegment(group.key, g.newer, feed.blocks) &&
                            dayBelongsToSegment(nextGroup.key, g.older, feed.blocks)) {
                            nodes.push(_jsx(TimelineGap, { newerSegmentId: g.newer.id, olderSegmentId: g.older.id, label: gapLabel(g.newer, g.older), typeFilter: typeFilter }, `gap-${g.newer.id}-${g.older.id}-after-${group.key}`));
                            break;
                        }
                    }
                }
                return nodes;
            }), footer, _jsx(StreamLoadMore, { hasMore: hasMore, isFetchingNextPage: isFetchingNextPage, onFetchNextPage: fetchMore })] }));
    // When an explicit pin slot is configured, publish it to every
    // card via `PinSlotContext` so per-card pin affordances become a
    // single-action toggle against the same slot.
    const withPinSlot = (node) => pinSlotContextValue ? (_jsx(PinSlotContext.Provider, { value: pinSlotContextValue, children: node })) : (node);
    // Always wrap reorder-aware surfaces in a single `<DndContext>`.
    // Drag is the unified entry point for reorder (within scope) and
    // stack-drop (onto a stack tile / unfurled stack-member); long-
    // press still owns multiselect entry, gated inside
    // `useLongPressDrag`. Surfaces without a `surfaceKey` (currently
    // just the layer-1 Collections grid) render bare and pay no
    // dnd-kit mount cost.
    //
    // No drag-state Context Providers wrap the DndContext anymore.
    // The lifecycle slice lives on `PouchApp` (`state.drag`) and
    // consumers subscribe through narrow `app.useStore(...)` selectors
    // — this is what keeps Stream itself stable across 60Hz drag-move
    // updates instead of re-running its 2300-line jsx() body per frame
    // and forcing every memoized child through its props comparison.
    if (surfaceKey) {
        return withPinSlot(_jsx(DndContext, { sensors: sensors, collisionDetection: collisionDetectionStrategy, autoScroll: DND_AUTOSCROLL_OPTIONS, onDragStart: handleDragStart, onDragMove: handleDragMove, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, children: streamBody }));
    }
    return withPinSlot(streamBody);
});
